import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/api';
import { SalesEmployeeSelectWithArrow, ActiveUserSelectWithArrow } from '../components/SapNamePickFields';

/* ═══════════════════ constants ═══════════════════ */
const TABS        = ['Contents', 'Logistics', 'Accounting', 'Tax', 'Attachments'];
const CURRENCIES  = ['INR', 'USD', 'EUR', 'JPY', 'GBP'];
const STATUSES    = ['Draft', 'Open', 'Closed', 'Cancelled'];
const UOM_OPTIONS = ['Each', 'Kg', 'Lb', 'Box', 'Pallet', 'Meter', 'Foot', 'Liter', 'Gallon', 'Set'];
const PAY_TERMS   = ['', 'Net 15', 'Net 30', 'Net 45', 'Net 60', 'Net 90', 'Due on Receipt', 'COD'];
const PAY_METHODS = ['', 'Bank Transfer', 'Check', 'Cash', 'Credit Card', 'Letter of Credit'];
const LANGUAGES   = ['', 'English', 'Hindi', 'Tamil', 'Telugu', 'Kannada', 'Marathi', 'Bengali', 'Gujarati'];
const SHIPPING_TYPES = ['', 'Air', 'Road', 'Rail', 'Sea', 'Courier'];
const LINE_TYPES  = ['Item', 'Service'];
const SERIES_OPTS = ['Primary'];
const ITEM_SERVICE_TYPES = ['Item', 'Service'];
const SUMMARY_TYPES = ['No Summary', 'Summary', 'Detailed Summary'];
const SALE_ORDER_TYPES = ['Product', 'Service', 'Mixed'];
const PLACES_OF_SUPPLY = ['', 'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Delhi', 'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamilnadu', 'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal'];

/** SO Contents item picker: server page size (scroll or “Load more”; server caps at 500). */
const SO_ITEM_PAGE_SIZE = 200;
/** Contents tab: how many SO lines to render at once (rest via pagination). */
const SO_LINES_PAGE_SIZE_OPTIONS = [25, 50, 100];

const STATUS_COLORS = {
  Draft:     'bg-[#F0F4F3] text-[#6B7F7F]',
  Open:      'bg-[#EDF5F0] text-[#4A9E6E]',
  Closed:    'bg-[#EDF2F7] text-[#4A6FA5]',
  Cancelled: 'bg-[#FDF0EE] text-[#C4574B]',
};

const today = () => new Date().toISOString().slice(0, 10);

const blankLine = () => ({
  _key: Date.now() + Math.random(),
  line_type: 'Item', item_id: '', item_no: '', item_desc: '', quantity: '1.0000',
  uom: 'Each', unit_price: '0.0000', discount_pct: '0.00',
  tax_code: '', distr_rule: '', cogs_distr_rule: '', del_date: '', project: '', country_of_origin: '',
  standard_item_id: '', commodity_classification: '',
  warehouse_id: '', wh_code: '', required_date: '',
});

const blankSO = {
  so_number: '', series: 'Primary', customer_name: '', contact_person: '',
  customer_ref_no: '', currency: 'INR',
  posting_date: today(), delivery_date: '', document_date: today(),
  status: 'Draft', remarks: '',
  item_service_type: 'Item', summary_type: 'No Summary',
  tax_pct: '12.00', freight_charges: '0.0000',
  // Logistics
  ship_to: '', bill_to: '', ship_to_code: '', bill_to_code: '', shipping_type: '', print_picking_sheet: false,
  language: 'English', procure_non_dropship: false, procure_dropship: false,
  approved: true, allow_partial_delivery: true, use_bill_to_for_tax: false, pick_pack_remarks: '',
  bp_channel_name: '', bp_channel_contact: '',
  // Accounting
  journal_remark: '', payment_terms: '', payment_method: '', central_bank_ind: '',
  due_date_type: '', due_date_months: '0', due_date_days: '0',
  cash_discount_offset: '0', use_shipped_goods_acct: false,
  bp_project: '', create_qr_code_from: '', blanket_agreement: '',
  cancellation_date: '', required_date: '', indicator: '',
  federal_tax_id: '', order_number: '', referenced_document: '',
  // Tax (India GST)
  transaction_category: '', form_no: '', duty_status: '',
  export_flag: false, supply_sec7_igst: false, differential_tax_rate_pct: '',
  // Footer
  sales_employee: '', owner: '', discount_pct: '0.00',
  rounding: false, rounding_amount: '0.0000',
  // New header fields
  place_of_supply: '', branch: '', sale_order_type: 'Product', branch_reg_no: '',
};

/* ═══════════════════ YellowArrow ═══════════════════ */
const YellowArrow = ({ onClick, title }) => (
  <button type="button" onClick={onClick} title={title}
    className="flex-shrink-0 flex items-center justify-center w-5 h-5 hover:opacity-80 transition-opacity">
    <svg width="12" height="12" viewBox="0 0 14 14" className="drop-shadow-sm">
      <polygon points="1,1 13,7 1,13" fill="#E8A000" stroke="#C47800" strokeWidth="0.8" strokeLinejoin="round" />
    </svg>
  </button>
);

/* ═══════════════════ ThreeDotsPicker ═══════════════════ */
const ThreeDotsPicker = ({ onClick, title }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    className="absolute right-1.5 top-1/2 -translate-y-1/2 px-1 text-sap-textLight hover:text-sap-blue transition-colors"
  >
    <span className="text-base leading-none">⋯</span>
  </button>
);

/* ═══════════════════ ProjectPickerDialog ═══════════════════ */
const ProjectPickerDialog = ({ current, onSelect, onClose }) => {
  const [search, setSearch] = React.useState('');
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [selected, setSelected] = React.useState(null);
  const [showNew, setShowNew] = React.useState(false);
  const [newCode, setNewCode] = React.useState('');
  const [newName, setNewName] = React.useState('');
  const [creating, setCreating] = React.useState(false);
  const [createErr, setCreateErr] = React.useState('');

  const reload = React.useCallback(async (q) => {
    try {
      setLoading(true);
      const r = await api.get(`/projects?search=${encodeURIComponent(q || '')}`);
      setRows(r || []);
    } catch (e) {
      setError(e.message || 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(() => { if (!cancelled) reload(search); }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [search, reload]);

  const handleChoose = () => {
    if (selected) onSelect(selected);
  };

  const handleNew = async () => {
    if (!newCode.trim()) { setCreateErr('Project code is required'); return; }
    setCreating(true); setCreateErr('');
    try {
      const created = await api.post('/projects', { project_code: newCode.trim(), project_name: newName.trim() });
      setShowNew(false);
      setNewCode(''); setNewName('');
      await reload(search);
      onSelect(created);
    } catch (e) {
      setCreateErr(e.message || 'Failed to create project');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl border border-sap-border w-[720px] max-h-[560px] flex flex-col"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-2.5 bg-sap-blue rounded-t-xl">
          <span className="text-sap-text text-xs font-semibold">List of Projects</span>
          <button onClick={onClose} className="text-sap-textLight hover:text-sap-text text-base leading-none">✕</button>
        </div>
        <div className="px-4 py-2 border-b border-sap-border">
          <input autoFocus value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Find project code or name…"
            className="w-full h-7 px-2 text-xs border border-sap-border rounded focus:outline-none focus:border-sap-blue" />
        </div>
        <div className="overflow-y-auto flex-1">
          {loading ? (
            <div className="py-10 text-center text-xs text-sap-textLight">Loading…</div>
          ) : error ? (
            <div className="py-10 text-center text-xs text-red-600">{error}</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-sap-blue/5 border-b border-sap-border/40">
                <tr>
                  <th className="text-left px-3 py-2 text-2xs font-semibold text-sap-textLight uppercase tracking-wider w-40">Project Code</th>
                  <th className="text-left px-3 py-2 text-2xs font-semibold text-sap-textLight uppercase tracking-wider">Project Name</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p, i) => {
                  const isCurrent = String(p.project_code) === String(current);
                  const isSel = selected && String(selected.project_code) === String(p.project_code);
                  return (
                    <tr key={p.project_code || i}
                      onClick={() => setSelected(p)}
                      onDoubleClick={() => onSelect(p)}
                      className={`border-t border-sap-border/20 cursor-pointer transition-colors
                        ${isSel ? 'bg-sap-blue/15 ring-1 ring-sap-blue/30' : isCurrent ? 'bg-sap-blue/10 font-semibold' : 'hover:bg-sap-blue/5'}`}>
                      <td className="px-3 py-2 font-mono font-semibold text-sap-blue">{p.project_code}</td>
                      <td className="px-3 py-2 text-sap-text">{p.project_name || '—'}</td>
                    </tr>
                  );
                })}
                {rows.length === 0 && (
                  <tr><td colSpan={2} className="px-3 py-6 text-center text-sap-textLight">No projects found</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {showNew && (
          <div className="px-4 py-3 border-t border-sap-border bg-blue-50/50 space-y-2">
            <div className="text-xs font-semibold text-sap-text">New Project</div>
            <div className="flex gap-2">
              <input value={newCode} onChange={e => setNewCode(e.target.value)}
                placeholder="Project Code *" className="flex-1 h-7 px-2 text-xs border border-sap-border rounded focus:outline-none focus:border-sap-blue" />
              <input value={newName} onChange={e => setNewName(e.target.value)}
                placeholder="Project Name" className="flex-[2] h-7 px-2 text-xs border border-sap-border rounded focus:outline-none focus:border-sap-blue" />
            </div>
            {createErr && <div className="text-2xs text-red-600">{createErr}</div>}
            <div className="flex gap-2">
              <button onClick={handleNew} disabled={creating}
                className="px-3 py-1 text-xs font-semibold bg-sap-blue text-white rounded hover:bg-sap-blueDark disabled:opacity-50">
                {creating ? 'Creating…' : 'Create'}
              </button>
              <button onClick={() => { setShowNew(false); setCreateErr(''); }}
                className="px-3 py-1 text-xs text-sap-textLight border border-sap-border rounded hover:bg-gray-100">
                Cancel
              </button>
            </div>
          </div>
        )}

        <div className="px-4 py-2 border-t border-sap-border bg-gray-50 rounded-b-xl flex items-center justify-between">
          <span className="text-2xs text-sap-textLight">{rows.length} project{rows.length !== 1 ? 's' : ''}</span>
          <div className="flex gap-2">
            <button onClick={handleChoose} disabled={!selected}
              className="btn-primary-sm disabled:opacity-40">
              Choose
            </button>
            <button onClick={onClose}
              className="px-4 py-1.5 text-xs font-semibold border border-sap-border rounded-lg hover:bg-gray-100 transition-all">
              Cancel
            </button>
            <button onClick={() => setShowNew(v => !v)}
              className="px-4 py-1.5 text-xs font-semibold border border-sap-blue text-sap-blue rounded-lg hover:bg-sap-blue/5 transition-all">
              New
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════ SapReferencePickerDialog (Tax / Distribution) ═══════════════════ */
const SapReferencePickerDialog = ({ type, current, onSelect, onClose }) => {
  const isTax = type === 'tax';
  const title = isTax ? 'Tax Codes' : 'Distribution Rules';
  const [search, setSearch] = React.useState('');
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [selected, setSelected] = React.useState(current || '');

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const path = isTax ? '/sap-reference/tax-codes' : '/sap-reference/distribution-rules';
        const data = await api.get(`${path}?search=${encodeURIComponent(search)}&limit=200`);
        if (!cancelled) setRows(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [search, isTax]);

  const codeKey = isTax ? 'tax_code' : 'rule_code';
  const nameKey = isTax ? 'tax_name' : 'description';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl border border-sap-border w-[640px] max-h-[520px] flex flex-col"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-2.5 bg-sap-blue rounded-t-xl">
          <span className="text-sap-text text-xs font-semibold">{title}</span>
          <button type="button" onClick={onClose} className="text-sap-textLight hover:text-sap-text text-base leading-none">✕</button>
        </div>
        <div className="px-4 py-2 border-b border-sap-border">
          <input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={isTax ? 'Search code or name…' : 'Search rule or description…'}
            className="w-full h-7 px-2 text-xs border border-sap-border rounded focus:outline-none focus:border-sap-blue"
          />
        </div>
        <div className="overflow-y-auto flex-1">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-sap-blue/5 border-b border-sap-border/40">
              <tr>
                <th className="text-left px-3 py-1.5 text-2xs font-semibold text-sap-textLight uppercase tracking-wider w-28">Code</th>
                <th className="text-left px-3 py-1.5 text-2xs font-semibold text-sap-textLight uppercase tracking-wider">Name / Description</th>
                {isTax && (
                  <th className="text-right px-3 py-1.5 text-2xs font-semibold text-sap-textLight uppercase tracking-wider w-16">Rate %</th>
                )}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={isTax ? 3 : 2} className="px-3 py-4 text-center text-sap-textLight">Loading…</td></tr>
              )}
              {!loading && rows.map((r) => {
                const code = r[codeKey];
                const isCurrent = String(code) === String(selected);
                return (
                  <tr
                    key={code}
                    onClick={() => setSelected(code)}
                    onDoubleClick={() => onSelect(r)}
                    className={`border-t border-sap-border/20 cursor-pointer transition-colors
                      ${isCurrent ? 'bg-sap-blue/10 font-semibold' : 'hover:bg-sap-blue/5'}`}
                  >
                    <td className="px-3 py-1.5 text-sap-blue font-mono font-semibold">{code}</td>
                    <td className="px-3 py-1.5 text-sap-text">{r[nameKey] || '—'}</td>
                    {isTax && (
                      <td className="px-3 py-1.5 text-right text-sap-textLight">{Number(r.rate_pct).toFixed(2)}</td>
                    )}
                  </tr>
                );
              })}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={isTax ? 3 : 2} className="px-3 py-4 text-center text-sap-textLight">No records — import master data Excel files</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2 border-t border-sap-border bg-gray-50 rounded-b-xl flex justify-between items-center">
          <span className="text-2xs text-sap-textLight">Double-click or Choose • {rows.length} row{rows.length !== 1 ? 's' : ''}</span>
          <div className="flex gap-2">
            <button type="button" onClick={onClose}
              className="px-3 py-1 text-xs border border-sap-border rounded-lg hover:bg-gray-100">Cancel</button>
            <button type="button" disabled={!selected}
              onClick={() => {
                const row = rows.find((r) => String(r[codeKey]) === String(selected));
                if (row) onSelect(row);
              }}
              className="btn-primary-sm disabled:opacity-40">Choose</button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════ SapReferenceCell (input + golden arrow) ═══════════════════ */
const SapReferenceCell = ({ value, onChange, onOpen, title, width = 'w-20' }) => (
  <td className="px-1 py-1">
    <div className="flex items-center gap-0.5">
      <YellowArrow onClick={onOpen} title={title} />
      <input
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className={`${width} border border-sap-border rounded px-2 py-1 text-xs focus:outline-none focus:border-sap-blue`}
      />
    </div>
  </td>
);

/* ═══════════════════ WarehousePickerDialog ═══════════════════ */
const WarehousePickerDialog = ({ warehouses, currentWhId, itemNo, onSelect, onClose }) => {
  const [search, setSearch] = React.useState('');
  const filtered = warehouses.filter(w =>
    !search ||
    w.wh_code.toLowerCase().includes(search.toLowerCase()) ||
    w.wh_name.toLowerCase().includes(search.toLowerCase())
  );
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl border border-sap-border w-[600px] max-h-[480px] flex flex-col"
        onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2.5 bg-sap-blue rounded-t-xl">
          <span className="text-sap-text text-xs font-semibold">
            Select Warehouse{itemNo ? ` — ${itemNo}` : ''}
          </span>
          <button onClick={onClose} className="text-sap-textLight hover:text-sap-text text-base leading-none">✕</button>
        </div>
        {/* Search */}
        <div className="px-4 py-2 border-b border-sap-border">
          <input
            autoFocus
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by code or name…"
            className="w-full h-7 px-2 text-xs border border-sap-border rounded focus:outline-none focus:border-sap-blue"
          />
        </div>
        {/* Table */}
        <div className="overflow-y-auto flex-1">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-sap-blue/5 border-b border-sap-border/40">
              <tr>
                <th className="text-left px-3 py-1.5 text-2xs font-semibold text-sap-textLight uppercase tracking-wider w-16">Code</th>
                <th className="text-left px-3 py-1.5 text-2xs font-semibold text-sap-textLight uppercase tracking-wider">Warehouse Name</th>
                <th className="text-right px-3 py-1.5 text-2xs font-semibold text-sap-textLight uppercase tracking-wider w-20">In Stock</th>
                <th className="text-right px-3 py-1.5 text-2xs font-semibold text-sap-textLight uppercase tracking-wider w-20">Committed</th>
                <th className="text-right px-3 py-1.5 text-2xs font-semibold text-sap-textLight uppercase tracking-wider w-20">Available</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(w => {
                const isCurrent = String(w.wh_id) === String(currentWhId);
                return (
                  <tr key={w.wh_id}
                    onClick={() => onSelect(w)}
                    className={`border-t border-sap-border/20 cursor-pointer transition-colors
                      ${isCurrent ? 'bg-sap-blue/10 font-semibold' : 'hover:bg-sap-blue/5'}`}>
                    <td className="px-3 py-1.5 text-sap-blue font-mono font-semibold">{w.wh_code}</td>
                    <td className="px-3 py-1.5 text-sap-text">{w.wh_name}</td>
                    <td className="px-3 py-1.5 text-right text-sap-textLight">{w.in_stock != null ? Number(w.in_stock).toFixed(2) : '—'}</td>
                    <td className="px-3 py-1.5 text-right text-sap-textLight">{w.committed != null ? Number(w.committed).toFixed(2) : '—'}</td>
                    <td className={`px-3 py-1.5 text-right font-semibold ${Number(w.available) > 0 ? 'text-emerald-600' : 'text-sap-textLight'}`}>
                      {w.available != null ? Number(w.available).toFixed(2) : '—'}
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-4 text-center text-sap-textLight">No warehouses found</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {/* Footer */}
        <div className="px-4 py-2 border-t border-sap-border bg-gray-50 rounded-b-xl text-2xs text-sap-textLight">
          Click a row to select • {filtered.length} warehouse{filtered.length !== 1 ? 's' : ''}
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════ WhseCell ═══════════════════ */
const WhseCell = ({ line, itemWarehousesMap, warehouses, updateLine, idx }) => {
  const [open, setOpen] = React.useState(false);
  const itemWhs = line.item_id && itemWarehousesMap[line.item_id]
    ? itemWarehousesMap[line.item_id]
    : warehouses;
  const currentWh = itemWhs.find(w => String(w.wh_id) === String(line.warehouse_id))
    || warehouses.find(w => String(w.wh_id) === String(line.warehouse_id));

  const handleSelect = (wh) => {
    updateLine(idx, 'warehouse_id', String(wh.wh_id));
    updateLine(idx, 'wh_code', wh.wh_code);
    setOpen(false);
  };

  return (
    <td className="px-1 py-1">
      <div className="flex items-center gap-1">
        <YellowArrow
          title="Click to change warehouse"
          onClick={() => setOpen(true)}
        />
        <span
          className="text-xs text-sap-blue font-mono font-semibold cursor-pointer hover:underline min-w-[28px]"
          onClick={() => setOpen(true)}
        >
          {currentWh ? currentWh.wh_code : '—'}
        </span>
      </div>
      {open && (
        <WarehousePickerDialog
          warehouses={itemWhs}
          currentWhId={line.warehouse_id}
          itemNo={line.item_no}
          onSelect={handleSelect}
          onClose={() => setOpen(false)}
        />
      )}
    </td>
  );
};

const fmt4 = (v) => isNaN(+v) ? '0.0000' : Number(v).toFixed(4);
const fmt2 = (v) => isNaN(+v) ? '0.00' : Number(v).toFixed(2);
const fmtCurrency = (v) => Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/* ═══════════════════ inputs ═══════════════════ */
const inp = `w-full h-[28px] px-2 text-xs border border-sap-border rounded bg-white
             focus:outline-none focus:ring-1 focus:ring-sap-blue/40 focus:border-sap-blue`;
const sel = `${inp} appearance-none`;
const lbl = 'block text-[10px] font-medium text-sap-textLight mb-0.5 whitespace-nowrap';
const chk = 'accent-sap-blue w-3.5 h-3.5';

export default function SalesOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const [form, setForm]       = useState({ ...blankSO });
  const [lines, setLines]     = useState([blankLine()]);
  const [attachments, setAttachments] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [soNoCopied, setSoNoCopied] = useState(false);

  const [itemSearchIdx, setItemSearchIdx] = useState(null);
  const [itemResults, setItemResults]     = useState([]);
  const [itemSearch, setItemSearch]       = useState('');
  const [itemDDSearch, setItemDDSearch]   = useState('');
  const [itemListLoading, setItemListLoading] = useState(false);
  const [itemListLoadingMore, setItemListLoadingMore] = useState(false);
  const [itemListHasMore, setItemListHasMore] = useState(false);
  const itemListPageRef = useRef(1);
  const itemListHasMoreRef = useRef(false);
  const itemPickerFetchGenRef = useRef(0);
  /** item_id → row from API (all pages merged) so “Add selected” works beyond visible list */
  const pickerItemByIdRef = useRef(new Map());
  const [showItemDD, setShowItemDD]       = useState(false);
  const [itemDDPos, setItemDDPos]         = useState({ bottom: 0, left: 0 });
  /** Multi-select in item dropdown: Set of item_id */
  const [itemDDSelectedIds, setItemDDSelectedIds] = useState(() => new Set());
  const [itemPickerSelectAllBusy, setItemPickerSelectAllBusy] = useState(false);
  /** Contents grid: paginate rendered rows so huge orders do not freeze the browser */
  const [linesPage, setLinesPage] = useState(1);
  const [linesPerPage, setLinesPerPage] = useState(25);
  const [warehouses, setWarehouses]       = useState([]);
  // Map of item_id → array of warehouses that carry that item
  const [itemWarehousesMap, setItemWarehousesMap] = useState({});
  const [refPicker, setRefPicker] = useState(null); // { type: 'tax'|'distribution', field, idx }

  // Customer search
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [showCustomerDD, setShowCustomerDD] = useState(false);

  /* ── Load warehouses ── */
  useEffect(() => {
    api.get('/sales-orders/warehouses')
      .then(r => setWarehouses(r || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const maxP = Math.max(1, Math.ceil(lines.length / linesPerPage));
    setLinesPage((p) => (p > maxP ? maxP : p));
  }, [lines.length, linesPerPage]);

  /* ── Load existing SO ── */
  useEffect(() => {
    if (isNew) {
      api.get('/sales-orders/next-number')
        .then(r => {
          const next = r?.next || '';
          setForm(f => ({ ...f, so_number: next, bp_project: next }));
        })
        .catch(() => {});
      return;
    }
    (async () => {
      try {
        const so = await api.get(`/sales-orders/${id}`);
        const h = { ...blankSO };
        for (const k of Object.keys(blankSO)) {
          if (so[k] !== undefined && so[k] !== null) {
            if (typeof blankSO[k] === 'boolean') h[k] = !!so[k];
            else h[k] = so[k];
          }
        }
        // date fields
        for (const dk of ['posting_date','delivery_date','document_date','cancellation_date','required_date']) {
          h[dk] = so[dk] ? String(so[dk]).slice(0, 10) : '';
          if (h[dk] === '0000-00-00' || h[dk] === 'null') h[dk] = '';
        }
        h.tax_pct = fmt2(so.tax_pct);
        h.freight_charges = fmt4(so.freight_charges);
        h.discount_pct = fmt2(so.discount_pct);
        h.rounding_amount = fmt4(so.rounding_amount);
        h.differential_tax_rate_pct = so.differential_tax_rate_pct != null ? fmt2(so.differential_tax_rate_pct) : '';
        h.export_flag = !!so.export_flag;
        h.supply_sec7_igst = !!so.supply_sec7_igst;
        h.use_bill_to_for_tax = !!so.use_bill_to_for_tax;
        setForm(h);
        setLines((so.lines || []).map(l => ({
          _key: l.line_id,
          line_type: l.line_type || 'Item',
          item_id: l.item_id, item_no: l.item_no, item_desc: l.item_desc,
          quantity: fmt4(l.quantity), uom: l.uom,
          unit_price: fmt4(l.unit_price), discount_pct: fmt2(l.discount_pct),
          tax_code: l.tax_code || '', distr_rule: l.distr_rule || '', cogs_distr_rule: l.cogs_distr_rule || '',
          del_date: l.del_date?.slice(0, 10) || '',
          project: l.project || '', country_of_origin: l.country_of_origin || '',
          standard_item_id: l.standard_item_id || '', commodity_classification: l.commodity_classification || '',
          warehouse_id: l.warehouse_id || '', wh_code: l.wh_code || '',
          required_date: l.required_date?.slice(0, 10) || '',
        })));
        // Pre-load item warehouses for all line items
        const itemIds = [...new Set((so.lines || []).map(l => l.item_id).filter(Boolean))];
        const whMap = {};
        await Promise.all(itemIds.map(async (iid) => {
          try {
            const rows = await api.get(`/sales-orders/item-warehouses/${iid}`);
            whMap[iid] = rows || [];
          } catch { whMap[iid] = []; }
        }));
        setItemWarehousesMap(whMap);
        setAttachments(so.attachments || []);
      } catch {
        setError('Failed to load sales order');
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isNew]);

  /* ── Customer search ── */
  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/sales-orders/customers?search=${encodeURIComponent(customerSearch)}`);
        setCustomerResults(r || []);
      } catch { setCustomerResults([]); }
    }, 200);
    return () => clearTimeout(t);
  }, [customerSearch]);

  /* ── Item picker: paginated server search (page 1 resets on open / search change) ── */
  useEffect(() => {
    const openOrTyping = showItemDD || !!(itemSearch || '').trim();
    if (!openOrTyping) {
      return;
    }
    const q = showItemDD ? itemDDSearch : itemSearch;
    const fetchGen = ++itemPickerFetchGenRef.current;
    const t = setTimeout(async () => {
      setItemListLoading(true);
      itemListPageRef.current = 1;
      itemListHasMoreRef.current = false;
      setItemListHasMore(false);
      try {
        const r = await api.get(
          `/sales-orders/items?search=${encodeURIComponent((q || '').trim())}&page=1&limit=${SO_ITEM_PAGE_SIZE}`
        );
        if (fetchGen !== itemPickerFetchGenRef.current) return;
        const items = Array.isArray(r?.items) ? r.items : Array.isArray(r) ? r : [];
        const hasMore = Boolean(r?.has_more);
        const m = new Map();
        items.forEach((it) => {
          const iid = Number(it.item_id);
          if (Number.isFinite(iid)) m.set(iid, it);
        });
        pickerItemByIdRef.current = m;
        setItemResults(items);
        setItemListHasMore(hasMore);
        itemListHasMoreRef.current = hasMore;
      } catch {
        if (fetchGen !== itemPickerFetchGenRef.current) return;
        setItemResults([]);
        setItemListHasMore(false);
        itemListHasMoreRef.current = false;
        pickerItemByIdRef.current = new Map();
      } finally {
        if (fetchGen === itemPickerFetchGenRef.current) setItemListLoading(false);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [itemSearch, itemDDSearch, showItemDD]);

  const loadMoreSoItems = useCallback(async () => {
    if (itemListLoading || itemListLoadingMore || !itemListHasMoreRef.current) return;
    const q = showItemDD ? itemDDSearch : itemSearch;
    const nextPage = itemListPageRef.current + 1;
    const fetchGen = itemPickerFetchGenRef.current;
    setItemListLoadingMore(true);
    try {
      const r = await api.get(
        `/sales-orders/items?search=${encodeURIComponent((q || '').trim())}&page=${nextPage}&limit=${SO_ITEM_PAGE_SIZE}`
      );
      if (fetchGen !== itemPickerFetchGenRef.current) return;
      const items = Array.isArray(r?.items) ? r.items : [];
      const hasMore = Boolean(r?.has_more);
      items.forEach((it) => {
        const iid = Number(it.item_id);
        if (Number.isFinite(iid)) pickerItemByIdRef.current.set(iid, it);
      });
      setItemResults((prev) => [...prev, ...items]);
      itemListPageRef.current = nextPage;
      setItemListHasMore(hasMore);
      itemListHasMoreRef.current = hasMore;
    } catch {
      /* keep list */
    } finally {
      if (fetchGen === itemPickerFetchGenRef.current) setItemListLoadingMore(false);
    }
  }, [itemDDSearch, itemSearch, itemListLoading, itemListLoadingMore]);

  const onItemPickerScroll = useCallback(
    (e) => {
      if (itemListLoading || itemListLoadingMore || !itemListHasMoreRef.current) return;
      const el = e.currentTarget;
      const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 56;
      if (nearBottom) loadMoreSoItems();
    },
    [itemListLoading, itemListLoadingMore, loadMoreSoItems]
  );

  /** Close item picker only if focus did not move into the floating panel (see autoFocus on inner search). */
  const scheduleCloseItemPickerIfFocusOutside = useCallback(() => {
    setTimeout(() => {
      const panel = document.getElementById('so-item-picker-panel');
      if (panel && panel.contains(document.activeElement)) return;
      setShowItemDD(false);
    }, 150);
  }, []);

  /** Fetch every page for current search and select all item_ids (details stored in pickerItemByIdRef). */
  const selectAllMatchingPickerItems = useCallback(async () => {
    const q = String((showItemDD ? itemDDSearch : itemSearch) ?? '').trim();
    const fetchSnapshot = itemPickerFetchGenRef.current;
    if (!window.confirm(
      'Load every item that matches this search from the server and select them all? Large lists may take a minute.'
    )) return;
    setItemPickerSelectAllBusy(true);
    try {
      let page = 1;
      let hasMore = true;
      const orderedIds = [];
      const fullMap = new Map();
      while (hasMore) {
        if (fetchSnapshot !== itemPickerFetchGenRef.current) return;
        const r = await api.get(
          `/sales-orders/items?search=${encodeURIComponent(q)}&page=${page}&limit=${SO_ITEM_PAGE_SIZE}`
        );
        if (fetchSnapshot !== itemPickerFetchGenRef.current) return;
        const items = Array.isArray(r?.items) ? r.items : [];
        hasMore = Boolean(r?.has_more);
        items.forEach((it) => {
          const iid = Number(it.item_id);
          if (!Number.isFinite(iid)) return;
          if (!fullMap.has(iid)) {
            fullMap.set(iid, it);
            orderedIds.push(iid);
          }
        });
        page += 1;
      }
      fullMap.forEach((v, k) => pickerItemByIdRef.current.set(k, v));
      setItemDDSelectedIds(new Set(orderedIds));
    } catch {
      /* keep prior selection */
    } finally {
      setItemPickerSelectAllBusy(false);
    }
  }, [showItemDD, itemDDSearch, itemSearch]);

  /** Select only rows currently in the dropdown list (no extra requests). */
  const selectVisiblePickerItems = useCallback(() => {
    setItemDDSelectedIds((prev) => {
      const next = new Set(prev);
      itemResults.forEach((it) => {
        const id = Number(it.item_id);
        if (Number.isFinite(id)) {
          next.add(id);
          pickerItemByIdRef.current.set(id, it);
        }
      });
      return next;
    });
  }, [itemResults]);

  /* ── Totals ── */
  const { subtotal, discountAmt, totalBeforeDiscount, taxAmt, freight, roundingVal, grandTotal } = useMemo(() => {
    const totalBeforeDiscount = lines.reduce((sum, l) => {
      const qty  = parseFloat(l.quantity) || 0;
      const price = parseFloat(l.unit_price) || 0;
      const disc  = parseFloat(l.discount_pct) || 0;
      return sum + qty * price * (1 - disc / 100);
    }, 0);
    const discPct = parseFloat(form.discount_pct) || 0;
    const discountAmt = totalBeforeDiscount * discPct / 100;
    const subtotal = totalBeforeDiscount - discountAmt;
    const taxAmt  = subtotal * (parseFloat(form.tax_pct) || 0) / 100;
    const freight = parseFloat(form.freight_charges) || 0;
    const roundingVal = form.rounding ? (parseFloat(form.rounding_amount) || 0) : 0;
    return { subtotal, discountAmt, totalBeforeDiscount, taxAmt, freight, roundingVal, grandTotal: subtotal + taxAmt + freight + roundingVal };
  }, [lines, form.tax_pct, form.freight_charges, form.discount_pct, form.rounding, form.rounding_amount]);

  const setField = (k, v) => setForm((f) => {
    const next = { ...f, [k]: v };
    if (isNew && k === 'so_number') {
      next.bp_project = String(v ?? '').trim();
    }
    return next;
  });

  const copySoNumber = useCallback(async () => {
    const t = String(form.so_number || '').trim();
    if (!t) return;
    try {
      await navigator.clipboard.writeText(t);
    } catch {
      try {
        const ta = document.createElement('textarea');
        ta.value = t;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      } catch {
        return;
      }
    }
    setSoNoCopied(true);
    setTimeout(() => setSoNoCopied(false), 2000);
  }, [form.so_number]);

  const updateLine = (idx, k, v) => setLines(ls => ls.map((l, i) => i === idx ? { ...l, [k]: v } : l));
  const addLine    = () => setLines(ls => [...ls, blankLine()]);
  const removeLine = (idx) => setLines(ls => ls.filter((_, i) => i !== idx));

  /* ── Fetch warehouses for an item and cache in map ── */
  const loadItemWarehouses = async (item_id) => {
    if (!item_id || itemWarehousesMap[item_id]) return;
    try {
      const rows = await api.get(`/sales-orders/item-warehouses/${item_id}`);
      setItemWarehousesMap(m => ({ ...m, [item_id]: rows || [] }));
    } catch { /* silent */ }
  };

  const pickItem = (item) => {
    if (itemSearchIdx === null) return;
    const iid = Number(item.item_id);
    if (Number.isFinite(iid)) pickerItemByIdRef.current.set(iid, item);
    setLines(ls => ls.map((l, i) => i === itemSearchIdx ? {
      ...l,
      item_id: item.item_id, item_no: item.item_no, item_desc: item.description,
      uom: item.uom || 'Each', unit_price: fmt4(item.unit_price || 0),
      warehouse_id: item.default_wh_id || '', wh_code: item.default_wh_code || '',
    } : l));
    loadItemWarehouses(item.item_id);
    setShowItemDD(false); setItemSearch(''); setItemSearchIdx(null); setItemDDSearch(''); setItemDDSelectedIds(new Set());
  };

  const toggleItemDDSelect = (itemId) => {
    const id = Number(itemId);
    if (!Number.isFinite(id)) return;
    const row = itemResults.find((it) => Number(it.item_id) === id);
    if (row) pickerItemByIdRef.current.set(id, row);
    setItemDDSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  /** Apply checkbox selection: first item → current row; rest → new rows inserted below. */
  const addSelectedItemsFromPicker = () => {
    if (itemSearchIdx === null) return;
    const selectedItems = [...itemDDSelectedIds]
      .map((id) => pickerItemByIdRef.current.get(Number(id)))
      .filter(Boolean);
    if (!selectedItems.length) return;

    setLines((ls) => {
      const out = [...ls];
      const start = itemSearchIdx;
      selectedItems.forEach((item, i) => {
        const patch = {
          item_id: item.item_id,
          item_no: item.item_no,
          item_desc: item.description,
          uom: item.uom || 'Each',
          unit_price: fmt4(item.unit_price || 0),
          warehouse_id: item.default_wh_id || '',
          wh_code: item.default_wh_code || '',
        };
        if (i === 0) {
          out[start] = { ...out[start], ...patch };
        } else {
          out.splice(start + i, 0, { ...blankLine(), ...patch });
        }
      });
      return out;
    });
    setShowItemDD(false);
    setItemSearch('');
    setItemSearchIdx(null);
    setItemDDSearch('');
    setItemDDSelectedIds(new Set());

    const whChunk = 40;
    void (async () => {
      for (let i = 0; i < selectedItems.length; i += whChunk) {
        await Promise.all(selectedItems.slice(i, i + whChunk).map((item) => loadItemWarehouses(item.item_id)));
      }
    })();
  };

  const handleSave = async (statusOverride) => {
    if (!form.customer_name.trim()) { setError('Customer name is required'); return; }
    const validLines = lines.filter(l => l.item_id);
    if (validLines.length === 0) { setError('Add at least one line item'); return; }

    setSaving(true); setError(''); setSuccessMsg('');
    try {
      const payload = {
        ...form,
        status: statusOverride || form.status,
        lines: validLines.map(l => ({
          line_type: l.line_type || 'Item',
          item_id: l.item_id, quantity: l.quantity, uom: l.uom,
          unit_price: l.unit_price, discount_pct: l.discount_pct,
          tax_code: l.tax_code || null, distr_rule: l.distr_rule || null, cogs_distr_rule: l.cogs_distr_rule || null,
          del_date: l.del_date || null, project: l.project || null,
          country_of_origin: l.country_of_origin || null, standard_item_id: l.standard_item_id || null,
          commodity_classification: l.commodity_classification || null,
          warehouse_id: l.warehouse_id || null, required_date: l.required_date || null,
        })),
      };
      if (isNew) {
        const r = await api.post('/sales-orders', payload);
        const newId = r?.so_id;
        setSuccessMsg('Sales order created!');
        setTimeout(() => navigate(`/sales-order/${newId}`), 800);
      } else {
        await api.put(`/sales-orders/${id}`, payload);
        setSuccessMsg('Saved successfully');
        setTimeout(() => setSuccessMsg(''), 2500);
        if (statusOverride) setField('status', statusOverride);
      }
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this sales order?')) return;
    try {
      await api.del(`/sales-orders/${id}`);
      navigate('/sales/orders');
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to delete');
    }
  };

  if (loading) return <div className="py-20 text-center text-xs text-sap-textLight">Loading…</div>;

  const linesPageStart = (linesPage - 1) * linesPerPage;
  const linesTotalPages = Math.max(1, Math.ceil(lines.length / linesPerPage));
  const linesPageSlice = lines.slice(linesPageStart, linesPageStart + linesPerPage);

  /* ═══════════════════ RENDER ═══════════════════ */
  return (
    <div className="space-y-2 pb-6">
      {/* Breadcrumb */}
      <div className="text-2xs text-sap-textLight flex items-center gap-1">
        <button onClick={() => navigate('/sales/orders')} className="hover:text-sap-blue transition-colors">Sales Orders</button>
        <span>›</span>
        <span className="text-sap-text font-medium">{isNew ? 'New' : form.so_number}</span>
      </div>

      {/* Header row */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <h1 className="page-title">
            Sales Order
          </h1>
          {!isNew && (
            <span className={`px-2 py-0.5 rounded-full text-2xs font-semibold ${STATUS_COLORS[form.status] || ''}`}>
              {form.status}
            </span>
          )}
        </div>
        <div className="flex gap-1.5">
          {!isNew && form.status === 'Draft' && (
            <button onClick={() => handleSave('Open')} disabled={saving}
              className="btn-primary text-xs py-1.5 px-3 disabled:opacity-60">
              Confirm Order
            </button>
          )}
          <button onClick={() => handleSave()} disabled={saving}
            className="btn-primary text-xs py-1.5 px-3 disabled:opacity-60">
            {saving ? 'Saving…' : 'Save'}
          </button>
          {!isNew && (
            <button onClick={handleDelete}
              className="px-3 py-1 text-xs border border-red-300 text-red-500 rounded-lg hover:bg-red-50 transition-colors font-semibold">
              Delete
            </button>
          )}
          <button onClick={() => navigate('/sales/orders')}
            className="px-3 py-1 text-xs border border-sap-border text-sap-textLight rounded-lg hover:bg-gray-50 transition-colors">
            Cancel
          </button>
        </div>
      </div>

      {error   && <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-600">{error}</div>}
      {successMsg && <div className="px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-700">{successMsg}</div>}

      {/* ════════════ PERSISTENT HEADER ════════════ */}
      <div className="bg-white rounded-2xl shadow-sm p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
          {/* Left column */}
          <div className="space-y-2">
            <F label="Customer *">
              <div className="relative">
                <input
                  value={showCustomerDD ? customerSearch : form.customer_name}
                  onChange={e => {
                    setCustomerSearch(e.target.value);
                    setField('customer_name', e.target.value);
                    setShowCustomerDD(true);
                    api.get(`/sales-orders/customers?search=${encodeURIComponent(e.target.value)}`).then(r => setCustomerResults(r || [])).catch(() => {});
                  }}
                  onFocus={() => {
                    setCustomerSearch(form.customer_name);
                    setShowCustomerDD(true);
                    api.get(`/sales-orders/customers?search=${encodeURIComponent(form.customer_name)}`).then(r => setCustomerResults(r || [])).catch(() => {});
                  }}
                  onBlur={() => setTimeout(() => setShowCustomerDD(false), 150)}
                  placeholder="Search customer…"
                  className={`${inp} pr-7`}
                />
                <button type="button"
                  onMouseDown={e => {
                    e.preventDefault();
                    setCustomerSearch('');
                    setShowCustomerDD(true);
                    api.get('/sales-orders/customers').then(r => setCustomerResults(r || [])).catch(() => {});
                  }}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 text-sap-textLight hover:text-sap-blue transition-colors">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>
                {showCustomerDD && customerResults.length > 0 && (
                  <div className="absolute z-50 left-0 top-full mt-0.5 w-full bg-white border border-sap-border rounded-lg shadow-xl max-h-48 overflow-y-auto">
                    {customerResults.map((c, i) => (
                      <button key={i}
                        onMouseDown={() => { setField('customer_name', c); setShowCustomerDD(false); setCustomerSearch(''); }}
                        className="w-full text-left px-3 py-1.5 text-xs hover:bg-sap-blue/10 text-sap-text">
                        {c}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </F>
            <F label="Name">
              <input value={form.contact_person} onChange={e => setField('contact_person', e.target.value)}
                placeholder="Contact name" className={inp} />
            </F>
            <F label="Contact Person">
              <input value={form.contact_person} onChange={e => setField('contact_person', e.target.value)}
                className={inp} />
            </F>
            <F label="Customer Ref. No.">
              <input value={form.customer_ref_no} onChange={e => setField('customer_ref_no', e.target.value)}
                className={inp} />
            </F>
            <F label="Local Currency">
              <select value={form.currency} onChange={e => setField('currency', e.target.value)} className={sel}>
                {CURRENCIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </F>
            <F label="Place of Supply">
              <select value={form.place_of_supply} onChange={e => setField('place_of_supply', e.target.value)} className={sel}>
                {PLACES_OF_SUPPLY.map(p => <option key={p} value={p}>{p || '—'}</option>)}
              </select>
            </F>
            <F label="Branch">
              <input value={form.branch} onChange={e => setField('branch', e.target.value)}
                placeholder="Branch name" className={inp} />
            </F>
          </div>

          {/* Right column */}
          <div className="space-y-2">
            <F label="No.">
              <div className="flex gap-1 items-center">
                <input value={form.series} onChange={e => setField('series', e.target.value)}
                  placeholder="Series" className="w-[70px] h-[28px] px-2 text-xs border border-sap-border rounded bg-white focus:outline-none focus:ring-1 focus:ring-sap-blue/40 focus:border-sap-blue flex-shrink-0 flex-grow-0" />
                <input value={form.so_number} readOnly={!isNew}
                  onChange={e => setField('so_number', e.target.value)}
                  className={`${inp} flex-1 min-w-0 h-[28px] ${isNew ? '' : 'bg-gray-50 text-sap-textLight'}`} />
                <button
                  type="button"
                  onClick={copySoNumber}
                  disabled={!String(form.so_number || '').trim()}
                  title="Copy order number"
                  className="h-[28px] px-2 shrink-0 text-2xs font-semibold rounded border border-sap-border bg-white text-sap-text
                             hover:bg-sap-bg hover:border-sap-blue disabled:opacity-40 disabled:cursor-not-allowed
                             focus:outline-none focus:ring-1 focus:ring-sap-blue/40">
                  {soNoCopied ? 'Copied' : 'Copy'}
                </button>
              </div>
            </F>
            <F label="Status">
              <select value={form.status} onChange={e => setField('status', e.target.value)} className={sel}>
                {STATUSES.map(s => <option key={s}>{s}</option>)}
              </select>
            </F>
            <F label="Posting Date *">
              <input type="date" value={form.posting_date} onChange={e => setField('posting_date', e.target.value)}
                className={inp} />
            </F>
            <F label="Delivery Date">
              <input type="date" value={form.delivery_date} onChange={e => setField('delivery_date', e.target.value)}
                className={inp} />
            </F>
            <F label="Document Date">
              <input type="date" value={form.document_date} onChange={e => setField('document_date', e.target.value)}
                className={inp} />
            </F>
            <F label="Sale Order Type">
              <select value={form.sale_order_type} onChange={e => setField('sale_order_type', e.target.value)} className={sel}>
                {SALE_ORDER_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </F>
            <F label="Branch Reg. No.">
              <input value={form.branch_reg_no} onChange={e => setField('branch_reg_no', e.target.value)}
                className={inp} />
            </F>
          </div>
        </div>
      </div>

      {/* ════════════ TABS ════════════ */}
      <div className="flex gap-0 border-b border-sap-border/60">
        {TABS.map((t, i) => (
          <button key={t} onClick={() => setActiveTab(i)}
            className={`px-4 py-2 text-xs font-medium transition-colors border-b-2 -mb-px
              ${activeTab === i ? 'border-sap-blue text-sap-blue' : 'border-transparent text-sap-textLight hover:text-sap-text'}`}>
            {t}
          </button>
        ))}
      </div>

      {/* ─── Tab 0 : Contents ─── */}
      {activeTab === 0 && (
        <div className="space-y-2">
          {/* Content-level dropdowns */}
          <div className="bg-white rounded-2xl shadow-sm p-3 flex justify-between items-end">
            <F label="Item/Service Type">
              <select value={form.item_service_type} onChange={e => setField('item_service_type', e.target.value)} className={`${sel} w-32`}>
                {ITEM_SERVICE_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </F>
            <F label="Summary Type">
              <select value={form.summary_type} onChange={e => setField('summary_type', e.target.value)} className={`${sel} w-40`}>
                {SUMMARY_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </F>
          </div>

          {/* Line items table */}
          <div className="bg-white rounded-2xl shadow-sm overflow-x-auto">
            <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-b border-sap-border/40 bg-sap-blue/5">
              <span className="text-2xs text-sap-text">
                {lines.length === 0 ? (
                  <>No lines</>
                ) : (
                  <>
                    Showing <strong>{linesPageStart + 1}</strong>–<strong>{linesPageStart + linesPageSlice.length}</strong>
                    {' '}of <strong>{lines.length}</strong> lines
                  </>
                )}
              </span>
              <div className="flex flex-wrap items-center gap-2">
                <label className="flex items-center gap-1.5 text-2xs text-sap-textLight whitespace-nowrap">
                  Rows per page
                  <select
                    value={linesPerPage}
                    onChange={(e) => {
                      setLinesPerPage(Number(e.target.value));
                      setLinesPage(1);
                    }}
                    className="h-7 px-1.5 text-2xs border border-sap-border rounded bg-white focus:outline-none focus:border-sap-blue"
                  >
                    {SO_LINES_PAGE_SIZE_OPTIONS.map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </label>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setLinesPage((p) => Math.max(1, p - 1))}
                    disabled={linesPage <= 1}
                    className="px-2 py-1 text-2xs font-semibold border border-sap-border rounded-md bg-white text-sap-text hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Prev
                  </button>
                  <span className="text-2xs text-sap-text tabular-nums px-1 min-w-[4.5rem] text-center">
                    {linesPage} / {linesTotalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() => setLinesPage((p) => Math.min(linesTotalPages, p + 1))}
                    disabled={linesPage >= linesTotalPages}
                    className="px-2 py-1 text-2xs font-semibold border border-sap-border rounded-md bg-white text-sap-text hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
            <table className="w-full text-xs min-w-[1600px]">
              <thead>
                <tr className="bg-sap-blue/5 border-b border-sap-border/40 text-sap-textLight">
                  <th className="text-left px-3 py-2 font-semibold text-2xs uppercase tracking-wider w-8">#</th>
                  <th className="text-left px-3 py-2 font-semibold text-2xs uppercase tracking-wider w-20">Type</th>
                  <th className="text-left px-3 py-2 font-semibold text-2xs uppercase tracking-wider">Item No.</th>
                  <th className="text-left px-3 py-2 font-semibold text-2xs uppercase tracking-wider">Item Description</th>
                  <th className="text-right px-3 py-2 font-semibold text-2xs uppercase tracking-wider w-20">Quantity</th>
                  <th className="text-left px-3 py-2 font-semibold text-2xs uppercase tracking-wider w-24">UoM Name</th>
                  <th className="text-right px-3 py-2 font-semibold text-2xs uppercase tracking-wider w-24">Unit Price</th>
                  <th className="text-right px-3 py-2 font-semibold text-2xs uppercase tracking-wider w-20">Discount %</th>
                  <th className="text-right px-3 py-2 font-semibold text-2xs uppercase tracking-wider w-28">Price after Discount</th>
                  <th className="text-left px-3 py-2 font-semibold text-2xs uppercase tracking-wider w-24">Tax Code</th>
                  <th className="text-left px-3 py-2 font-semibold text-2xs uppercase tracking-wider w-20">Distr. Rule</th>
                  <th className="text-left px-3 py-2 font-semibold text-2xs uppercase tracking-wider w-24">COGS Distr. Rule</th>
                  <th className="text-right px-3 py-2 font-semibold text-2xs uppercase tracking-wider w-24">Total (LC)</th>
                  <th className="text-left px-3 py-2 font-semibold text-2xs uppercase tracking-wider w-24">Whse</th>
                  <th className="text-left px-3 py-2 font-semibold text-2xs uppercase tracking-wider w-28">Del. Date</th>
                  <th className="text-left px-3 py-2 font-semibold text-2xs uppercase tracking-wider w-24">Project</th>
                  <th className="text-left px-3 py-2 font-semibold text-2xs uppercase tracking-wider w-36">Country/Region of Origin</th>
                  <th className="text-left px-3 py-2 font-semibold text-2xs uppercase tracking-wider w-40">Standard Item Identification</th>
                  <th className="text-left px-3 py-2 font-semibold text-2xs uppercase tracking-wider w-36">Commodity Classification</th>
                  <th className="w-8" />
                </tr>
              </thead>
              <tbody>
                {linesPageSlice.map((l, relIdx) => {
                  const idx = linesPageStart + relIdx;
                  const lineTotal = (parseFloat(l.quantity) || 0) * (parseFloat(l.unit_price) || 0) * (1 - (parseFloat(l.discount_pct) || 0) / 100);
                  return (
                    <tr key={l._key} className="border-t border-sap-border/30 hover:bg-sap-blueLight/20">
                      <td className="px-3 py-1 text-sap-textLight">{idx + 1}</td>
                      <td className="px-1 py-1">
                        <select value={l.line_type} onChange={e => updateLine(idx, 'line_type', e.target.value)}
                          className="w-20 border border-sap-border rounded px-1 py-1 text-xs bg-white focus:outline-none focus:border-sap-blue">
                          {LINE_TYPES.map(t => <option key={t}>{t}</option>)}
                        </select>
                      </td>
                      {/* Item search */}
                      <td className="px-1 py-1 relative">
                        <div className="flex items-center gap-0.5">
                          <input value={itemSearchIdx === idx ? itemSearch : l.item_no}
                            onChange={e => {
                              setItemSearch(e.target.value);
                              setItemSearchIdx(idx);
                              setShowItemDD(true);
                              setItemDDSearch(e.target.value);
                              setItemDDSelectedIds(new Set());
                            }}
                            onFocus={e => {
                              const rect = e.target.getBoundingClientRect();
                              setItemDDPos({ bottom: window.innerHeight - rect.top + 2, left: rect.left });
                              setItemSearchIdx(idx);
                              setItemSearch(l.item_no || '');
                              setItemDDSearch(l.item_no || '');
                              setItemDDSelectedIds(new Set());
                              setShowItemDD(true);
                            }}
                            onBlur={scheduleCloseItemPickerIfFocusOutside}
                            placeholder="Search…"
                            className="w-20 border border-sap-border rounded-l px-2 py-1 text-xs focus:outline-none focus:border-sap-blue" />
                          <button type="button"
                            onMouseDown={e => {
                              e.preventDefault();
                              const rect = e.currentTarget.previousSibling.getBoundingClientRect();
                              setItemDDPos({ bottom: window.innerHeight - rect.top + 2, left: rect.left });
                              setItemSearchIdx(idx);
                              setItemSearch('');
                              setItemDDSearch('');
                              setItemDDSelectedIds(new Set());
                              setShowItemDD(true);
                            }}
                            className="h-[26px] px-1 border border-l-0 border-sap-border rounded-r bg-gray-50 hover:bg-sap-blue/10 text-sap-textLight hover:text-sap-blue transition-colors flex items-center">
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="6 9 12 15 18 9" />
                            </svg>
                          </button>
                        </div>
                        {showItemDD && itemSearchIdx === idx && (() => (
                            <div id="so-item-picker-panel" style={{ position: 'fixed', bottom: itemDDPos.bottom, left: itemDDPos.left, width: 340, zIndex: 9999 }}
                              className="bg-white border border-sap-border rounded-lg shadow-xl flex flex-col" onMouseDown={e => e.preventDefault()}>
                              {/* Search box inside dropdown */}
                              <div className="px-2 py-1.5 border-b border-sap-border/40 flex-shrink-0">
                                <input
                                  autoFocus
                                  value={itemDDSearch}
                                  onChange={(e) => {
                                    setItemDDSearch(e.target.value);
                                    setItemDDSelectedIds(new Set());
                                  }}
                                  placeholder="Search item no. or description…"
                                  className="w-full h-6 px-2 text-xs border border-sap-border rounded focus:outline-none focus:border-sap-blue"
                                />
                              </div>
                              <div className="overflow-y-auto max-h-52" onScroll={onItemPickerScroll}>
                                {itemListLoading && (
                                  <div className="px-3 py-4 text-xs text-sap-textLight text-center">Loading items…</div>
                                )}
                                {!itemListLoading && itemResults.length === 0 && (
                                  <div className="px-3 py-2 text-xs text-sap-textLight">No items found</div>
                                )}
                                {!itemListLoading && itemResults.map(it => {
                                  const iid = Number(it.item_id);
                                  const checked = itemDDSelectedIds.has(iid);
                                  return (
                                    <div
                                      key={it.item_id}
                                      role="button"
                                      tabIndex={0}
                                      onDoubleClick={() => pickItem(it)}
                                      className="w-full text-left px-2 py-1 text-xs hover:bg-sap-blue/10 flex items-center gap-2 border-b border-sap-border/20 last:border-b-0"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => toggleItemDDSelect(it.item_id)}
                                        onClick={(ev) => ev.stopPropagation()}
                                        className={chk}
                                        title="Select for multi-add"
                                      />
                                      <span className="font-semibold text-sap-blue w-24 flex-shrink-0">{it.item_no}</span>
                                      <span className="text-sap-textLight truncate flex-1">{it.description}</span>
                                    </div>
                                  );
                                })}
                                {itemListLoadingMore && (
                                  <div className="px-3 py-2 text-2xs text-sap-textLight text-center border-t border-sap-border/10">
                                    Loading more…
                                  </div>
                                )}
                                {!itemListLoading && itemListHasMore && !itemListLoadingMore && (
                                  <div className="px-2 py-1.5 border-t border-sap-border/10 bg-sap-bg/30">
                                    <button
                                      type="button"
                                      onMouseDown={(e) => e.preventDefault()}
                                      onClick={() => loadMoreSoItems()}
                                      className="w-full py-1.5 text-2xs font-semibold rounded-md border border-sap-border bg-white text-sap-blue hover:bg-sap-blue/5"
                                    >
                                      Load more items…
                                    </button>
                                  </div>
                                )}
                              </div>
                              <div className="px-2 py-1.5 border-t border-sap-border/40 flex-shrink-0 flex flex-wrap items-center gap-2 justify-between bg-sap-bg/50">
                                <span className="text-2xs text-sap-textLight">
                                  {itemListLoading
                                    ? '…'
                                    : `${itemResults.length} item${itemResults.length !== 1 ? 's' : ''} loaded`}
                                  {!itemListLoading && itemListHasMore ? ' — scroll or use “Load more” below' : ''}
                                  {!itemListLoading && !itemListHasMore && itemResults.length > 0 ? ' — end of list' : ''}
                                  <span className="block mt-0.5 text-sap-textLight/90">
                                    <strong>Select all matching</strong> loads every page for this search, then ticks all. <strong>Select visible</strong> only toggles rows in the list. Use <strong>Add selected</strong> to insert lines.
                                  </span>
                                </span>
                                <div className="flex flex-wrap gap-1.5 justify-end">
                                  <button
                                    type="button"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => selectAllMatchingPickerItems()}
                                    disabled={itemListLoading || itemPickerSelectAllBusy}
                                    title="Download every matching page from the server, then select all (not limited to visible rows)"
                                    className="px-2 py-1 text-2xs font-semibold rounded-md border border-sap-blue bg-sap-blue text-white hover:bg-sap-blueDark disabled:opacity-40 disabled:cursor-not-allowed"
                                  >
                                    {itemPickerSelectAllBusy ? 'Loading all…' : 'Select all matching'}
                                  </button>
                                  <button
                                    type="button"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={selectVisiblePickerItems}
                                    disabled={itemListLoading || itemResults.length === 0}
                                    title="Add to selection only the rows currently listed (fast)"
                                    className="px-2 py-1 text-2xs font-semibold border border-sap-border rounded-md bg-white text-sap-text hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                  >
                                    Select visible
                                  </button>
                                  <button
                                    type="button"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => setItemDDSelectedIds(new Set())}
                                    className="px-2 py-1 text-2xs font-semibold border border-sap-border rounded-md bg-white text-sap-text hover:bg-gray-50"
                                  >
                                    Clear ticks
                                  </button>
                                  <button
                                    type="button"
                                    onMouseDown={(e) => e.preventDefault()}
                                    disabled={itemDDSelectedIds.size === 0}
                                    onClick={addSelectedItemsFromPicker}
                                    className="px-3 py-1 text-2xs font-semibold rounded-md bg-sap-blue text-white hover:bg-sap-blueDark disabled:opacity-40 disabled:cursor-not-allowed"
                                  >
                                    Add selected ({itemDDSelectedIds.size})
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))()}
                      </td>
                      <td className="px-1 py-1">
                        <input value={l.item_desc} readOnly
                          className="w-full border border-sap-border rounded px-2 py-1 text-xs bg-gray-50 text-sap-textLight min-w-[140px]" />
                      </td>
                      <td className="px-1 py-1">
                        <input type="number" value={l.quantity} onChange={e => updateLine(idx, 'quantity', e.target.value)}
                          className="w-20 border border-sap-border rounded px-2 py-1 text-xs text-right focus:outline-none focus:border-sap-blue" />
                      </td>
                      <td className="px-1 py-1">
                        <select value={l.uom} onChange={e => updateLine(idx, 'uom', e.target.value)}
                          className="w-24 border border-sap-border rounded px-2 py-1 text-xs focus:outline-none focus:border-sap-blue bg-white">
                          {UOM_OPTIONS.map(u => <option key={u}>{u}</option>)}
                        </select>
                      </td>
                      <td className="px-1 py-1">
                        <input type="number" value={l.unit_price} onChange={e => updateLine(idx, 'unit_price', e.target.value)}
                          className="w-24 border border-sap-border rounded px-2 py-1 text-xs text-right focus:outline-none focus:border-sap-blue" />
                      </td>
                      <td className="px-1 py-1">
                        <input type="number" value={l.discount_pct} onChange={e => updateLine(idx, 'discount_pct', e.target.value)}
                          className="w-16 border border-sap-border rounded px-2 py-1 text-xs text-right focus:outline-none focus:border-sap-blue" />
                      </td>
                      <td className="px-3 py-1 text-right font-semibold">{fmtCurrency(lineTotal)}</td>
                      <SapReferenceCell
                        value={l.tax_code}
                        onChange={(v) => updateLine(idx, 'tax_code', v)}
                        onOpen={() => setRefPicker({ type: 'tax', field: 'tax_code', idx })}
                        title="Tax Code"
                        width="w-24"
                      />
                      <SapReferenceCell
                        value={l.distr_rule}
                        onChange={(v) => updateLine(idx, 'distr_rule', v)}
                        onOpen={() => setRefPicker({ type: 'distribution', field: 'distr_rule', idx })}
                        title="Distribution Rule"
                        width="w-16"
                      />
                      <SapReferenceCell
                        value={l.cogs_distr_rule}
                        onChange={(v) => updateLine(idx, 'cogs_distr_rule', v)}
                        onOpen={() => setRefPicker({ type: 'distribution', field: 'cogs_distr_rule', idx })}
                        title="COGS Distribution Rule"
                        width="w-16"
                      />
                      <td className="px-3 py-1 text-right font-semibold">{fmtCurrency(lineTotal)}</td>
                      {/* ── Whse column with YellowArrow ── */}
                      <WhseCell
                        line={l}
                        itemWarehousesMap={itemWarehousesMap}
                        warehouses={warehouses}
                        updateLine={updateLine}
                        idx={idx}
                      />
                      <td className="px-1 py-1">
                        <input type="date" value={l.del_date} onChange={e => updateLine(idx, 'del_date', e.target.value)}
                          className="w-28 border border-sap-border rounded px-2 py-1 text-xs focus:outline-none focus:border-sap-blue" />
                      </td>
                      <td className="px-1 py-1">
                        <input value={l.project} onChange={e => updateLine(idx, 'project', e.target.value)}
                          className="w-24 border border-sap-border rounded px-2 py-1 text-xs focus:outline-none focus:border-sap-blue" />
                      </td>
                      <td className="px-1 py-1">
                        <input value={l.country_of_origin} onChange={e => updateLine(idx, 'country_of_origin', e.target.value)}
                          className="w-36 border border-sap-border rounded px-2 py-1 text-xs focus:outline-none focus:border-sap-blue" />
                      </td>
                      <td className="px-1 py-1">
                        <input value={l.standard_item_id} onChange={e => updateLine(idx, 'standard_item_id', e.target.value)}
                          className="w-40 border border-sap-border rounded px-2 py-1 text-xs focus:outline-none focus:border-sap-blue" />
                      </td>
                      <td className="px-1 py-1">
                        <input value={l.commodity_classification} onChange={e => updateLine(idx, 'commodity_classification', e.target.value)}
                          className="w-36 border border-sap-border rounded px-2 py-1 text-xs focus:outline-none focus:border-sap-blue" />
                      </td>
                      <td className="px-2 py-1">
                        <button onClick={() => removeLine(idx)} disabled={lines.length === 1}
                          className="text-red-400 hover:text-red-600 transition-colors disabled:opacity-30" title="Remove">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <button onClick={addLine}
            className="w-full py-1.5 border border-dashed border-sap-blue/40 rounded-xl text-xs text-sap-blue hover:bg-sap-blue/5 transition-colors font-medium">
            + Add Line
          </button>
        </div>
      )}

      {/* ─── Tab 1 : Logistics ─── */}
      {activeTab === 1 && (
        <div className="bg-white rounded-2xl shadow-sm p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
            {/* Left column */}
            <div className="space-y-3">
              <F label="Ship To">
                <textarea value={form.ship_to} onChange={e => setField('ship_to', e.target.value)}
                  rows={3} className={`${inp} h-auto py-1.5 resize-none`} placeholder="Shipping address" />
              </F>
              <F label="Ship To Code">
                <input value={form.ship_to_code} onChange={e => setField('ship_to_code', e.target.value)} className={inp} />
              </F>
              <F label="Bill To">
                <textarea value={form.bill_to} onChange={e => setField('bill_to', e.target.value)}
                  rows={3} className={`${inp} h-auto py-1.5 resize-none`} placeholder="Billing address" />
              </F>
              <F label="Bill To Code">
                <input value={form.bill_to_code} onChange={e => setField('bill_to_code', e.target.value)} className={inp} />
              </F>
              <F label="Shipping Type">
                <select value={form.shipping_type} onChange={e => setField('shipping_type', e.target.value)} className={sel}>
                  {SHIPPING_TYPES.map(s => <option key={s} value={s}>{s || '—'}</option>)}
                </select>
              </F>
              <F label="Language">
                <select value={form.language} onChange={e => setField('language', e.target.value)} className={sel}>
                  {LANGUAGES.map(l => <option key={l} value={l}>{l || '—'}</option>)}
                </select>
              </F>
              <F label="Pick and Pack Remarks">
                <textarea value={form.pick_pack_remarks} onChange={e => setField('pick_pack_remarks', e.target.value)}
                  rows={2} className={`${inp} h-auto py-1.5 resize-none`} />
              </F>
            </div>

            {/* Right column – checkboxes + BP fields */}
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <label className="flex items-center gap-2 text-xs text-sap-text">
                  <input type="checkbox" checked={form.print_picking_sheet} onChange={e => setField('print_picking_sheet', e.target.checked)} className={chk} />
                  Print Picking Sheet
                </label>
                <label className="flex items-center gap-2 text-xs text-sap-text">
                  <input type="checkbox" checked={form.procure_non_dropship} onChange={e => setField('procure_non_dropship', e.target.checked)} className={chk} />
                  Procure Non Drop-Ship Items
                </label>
                <label className="flex items-center gap-2 text-xs text-sap-text">
                  <input type="checkbox" checked={form.procure_dropship} onChange={e => setField('procure_dropship', e.target.checked)} className={chk} />
                  Procure Drop-Ship Items
                </label>
                <label className="flex items-center gap-2 text-xs text-sap-text">
                  <input type="checkbox" checked={form.approved} onChange={e => setField('approved', e.target.checked)} className={chk} />
                  Approved
                </label>
                <label className="flex items-center gap-2 text-xs text-sap-text">
                  <input type="checkbox" checked={form.allow_partial_delivery} onChange={e => setField('allow_partial_delivery', e.target.checked)} className={chk} />
                  Allow Partial Delivery
                </label>
                <label className="flex items-center gap-2 text-xs text-sap-text">
                  <input type="checkbox" checked={form.use_bill_to_for_tax} onChange={e => setField('use_bill_to_for_tax', e.target.checked)} className={chk} />
                  Use Bill to Address to Determine Tax
                </label>
              </div>
              <F label="BP Channel Name">
                <input value={form.bp_channel_name} onChange={e => setField('bp_channel_name', e.target.value)} className={inp} />
              </F>
              <F label="BP Channel Contact">
                <input value={form.bp_channel_contact} onChange={e => setField('bp_channel_contact', e.target.value)} className={inp} />
              </F>
            </div>
          </div>
        </div>
      )}

      {/* ─── Tab 2 : Accounting ─── */}
      {activeTab === 2 && (
        <div className="bg-white rounded-2xl shadow-sm p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
            {/* Left */}
            <div className="space-y-3">
              <F label="Journal Remark">
                <input value={form.journal_remark} onChange={e => setField('journal_remark', e.target.value)} className={inp} />
              </F>
              <F label="Payment Terms">
                <select value={form.payment_terms} onChange={e => setField('payment_terms', e.target.value)} className={sel}>
                  {PAY_TERMS.map(t => <option key={t} value={t}>{t || '—'}</option>)}
                </select>
              </F>
              <F label="Payment Method">
                <select value={form.payment_method} onChange={e => setField('payment_method', e.target.value)} className={sel}>
                  {PAY_METHODS.map(m => <option key={m} value={m}>{m || '—'}</option>)}
                </select>
              </F>
              <F label="Central Bank Ind.">
                <input value={form.central_bank_ind} onChange={e => setField('central_bank_ind', e.target.value)} className={inp} />
              </F>
              <div>
                <label className={lbl}>Manually Recalculate Due Date</label>
                <div className="flex gap-2">
                  <input value={form.due_date_type} onChange={e => setField('due_date_type', e.target.value)}
                    placeholder="Type" className={`${inp} w-28`} />
                  <div className="flex items-center gap-1">
                    <span className="text-2xs text-sap-textLight">Months</span>
                    <input type="number" value={form.due_date_months} onChange={e => setField('due_date_months', e.target.value)}
                      className={`${inp} w-14 text-center`} />
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-2xs text-sap-textLight">Days</span>
                    <input type="number" value={form.due_date_days} onChange={e => setField('due_date_days', e.target.value)}
                      className={`${inp} w-14 text-center`} />
                  </div>
                </div>
              </div>
              <F label="Cash Discount Date Offset">
                <input type="number" value={form.cash_discount_offset} onChange={e => setField('cash_discount_offset', e.target.value)}
                  className={inp} />
              </F>
              <label className="flex items-center gap-2 text-xs text-sap-text">
                <input type="checkbox" checked={form.use_shipped_goods_acct} onChange={e => setField('use_shipped_goods_acct', e.target.checked)} className={chk} />
                Use Shipped Goods Account
              </label>
            </div>

            {/* Right */}
            <div className="space-y-3">
              <F label="Business Partner Project">
                <BusinessPartnerProjectField value={form.bp_project} onChange={v => setField('bp_project', v)} />
              </F>
              <F label="Create QR Code From">
                <input value={form.create_qr_code_from} onChange={e => setField('create_qr_code_from', e.target.value)} className={inp} />
              </F>
              <F label="Cancellation Date">
                <input type="date" value={form.cancellation_date} onChange={e => setField('cancellation_date', e.target.value)} className={inp} />
              </F>
              <F label="Required Date">
                <input type="date" value={form.required_date} onChange={e => setField('required_date', e.target.value)} className={inp} />
              </F>
              <F label="Indicator">
                <input value={form.indicator} onChange={e => setField('indicator', e.target.value)} className={inp} />
              </F>
              <F label="Federal Tax ID">
                <input value={form.federal_tax_id} onChange={e => setField('federal_tax_id', e.target.value)} className={inp} />
              </F>
              <F label="Order Number">
                <input value={form.order_number} onChange={e => setField('order_number', e.target.value)} className={inp} />
              </F>
              <F label="Referenced Document">
                <input value={form.referenced_document} onChange={e => setField('referenced_document', e.target.value)} className={inp} />
              </F>
              <F label="Blanket Agreement">
                <input value={form.blanket_agreement} onChange={e => setField('blanket_agreement', e.target.value)} className={inp} />
              </F>
            </div>
          </div>
        </div>
      )}

      {/* ─── Tab 3 : Tax ─── */}
      {activeTab === 3 && (
        <div className="bg-white rounded-2xl shadow-sm p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
            <div className="space-y-3">
              <F label="Transaction Category">
                <input value={form.transaction_category} onChange={e => setField('transaction_category', e.target.value)} className={inp} />
              </F>
              <F label="Form No.">
                <input value={form.form_no} onChange={e => setField('form_no', e.target.value)} className={inp} />
              </F>
              <F label="Duty Status">
                <input value={form.duty_status} onChange={e => setField('duty_status', e.target.value)} className={inp} />
              </F>
            </div>
            <div className="space-y-3">
              <label className="flex items-center gap-2 text-xs text-sap-text">
                <input type="checkbox" checked={form.export_flag} onChange={e => setField('export_flag', e.target.checked)} className={chk} />
                Export
              </label>
              <label className="flex items-center gap-2 text-xs text-sap-text">
                <input type="checkbox" checked={form.supply_sec7_igst} onChange={e => setField('supply_sec7_igst', e.target.checked)} className={chk} />
                Supply Covered under Sec 7 of IGST Act
              </label>
              <F label="Differential % of Tax Rate">
                <input type="number" value={form.differential_tax_rate_pct} onChange={e => setField('differential_tax_rate_pct', e.target.value)} className={inp} />
              </F>
            </div>
          </div>
        </div>
      )}

      {/* ─── Tab 4 : Attachments ─── */}
      {activeTab === 4 && (
        <div className="bg-white rounded-2xl shadow-sm p-5 space-y-3">
          <div className="overflow-x-auto">
            <table className="w-full text-xs min-w-[600px]">
              <thead>
                <tr className="bg-sap-blue/5 border-b border-sap-border/40 text-sap-textLight">
                  <th className="text-left px-3 py-2 font-semibold text-2xs uppercase tracking-wider w-8">#</th>
                  <th className="text-left px-3 py-2 font-semibold text-2xs uppercase tracking-wider">Target Path</th>
                  <th className="text-left px-3 py-2 font-semibold text-2xs uppercase tracking-wider">File Name</th>
                  <th className="text-left px-3 py-2 font-semibold text-2xs uppercase tracking-wider w-28">Attachment Date</th>
                  <th className="text-left px-3 py-2 font-semibold text-2xs uppercase tracking-wider">Free Text</th>
                  <th className="text-center px-3 py-2 font-semibold text-2xs uppercase tracking-wider w-32">Copy to Target Doc.</th>
                </tr>
              </thead>
              <tbody>
                {attachments.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-6 text-sap-textLight">No attachments</td></tr>
                ) : attachments.map((a, i) => (
                  <tr key={a.attachment_id} className="border-t border-sap-border/30">
                    <td className="px-3 py-1.5">{i + 1}</td>
                    <td className="px-3 py-1.5 text-sap-textLight">{a.target_path || '—'}</td>
                    <td className="px-3 py-1.5 font-medium">{a.file_name}</td>
                    <td className="px-3 py-1.5">{a.attachment_date?.slice(0, 10) || a.uploaded_at?.slice(0, 10) || ''}</td>
                    <td className="px-3 py-1.5 text-sap-textLight">{a.free_text || ''}</td>
                    <td className="px-3 py-1.5 text-center">{a.copy_to_target ? '✓' : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex gap-2">
            <button className="px-3 py-1.5 text-xs border border-sap-border rounded-lg hover:bg-gray-50 transition-colors text-sap-text">
              Browse
            </button>
            <button className="px-3 py-1.5 text-xs border border-sap-border rounded-lg hover:bg-gray-50 transition-colors text-sap-text">
              Display
            </button>
            <button className="px-3 py-1.5 text-xs border border-red-200 text-red-500 rounded-lg hover:bg-red-50 transition-colors">
              Delete
            </button>
          </div>
        </div>
      )}

      {/* ════════════ PERSISTENT FOOTER ════════════ */}
      <div className="bg-white rounded-2xl shadow-sm p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Left – Employee / Owner / Remarks */}
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-3">
              <F label="Sales Employee">
                <SalesEmployeeSelectWithArrow
                  value={form.sales_employee}
                  onChange={(v) => setField('sales_employee', v)}
                  selectClassName={`flex-1 min-w-0 ${sel}`}
                />
              </F>
              <F label="Owner">
                <ActiveUserSelectWithArrow
                  value={form.owner}
                  onChange={(v) => setField('owner', v)}
                  usersUrl="/sales-orders/users"
                  selectClassName={`flex-1 min-w-0 ${sel}`}
                />
              </F>
            </div>
            <F label="Remarks">
              <textarea value={form.remarks} onChange={e => setField('remarks', e.target.value)} rows={2}
                className={`${inp} h-auto py-1.5 resize-none`} />
            </F>
          </div>

          {/* Right – Totals */}
          <div className="space-y-1 text-xs">
            <div className="flex justify-between items-center text-sap-textLight">
              <span>Total Before Discount</span>
              <span className="font-medium">{fmtCurrency(totalBeforeDiscount)}</span>
            </div>
            <div className="flex justify-between items-center text-sap-textLight gap-2">
              <span className="flex items-center gap-1">Discount %
                <input type="number" value={form.discount_pct} onChange={e => setField('discount_pct', e.target.value)}
                  className="w-14 border border-sap-border rounded px-1 py-0.5 text-xs text-right focus:outline-none focus:border-sap-blue" />
              </span>
              <span>-{fmtCurrency(discountAmt)}</span>
            </div>
            <div className="flex justify-between items-center text-sap-textLight">
              <span className="flex items-center gap-1">Freight
                <input type="number" value={form.freight_charges} onChange={e => setField('freight_charges', e.target.value)}
                  className="w-20 border border-sap-border rounded px-1 py-0.5 text-xs text-right focus:outline-none focus:border-sap-blue" />
              </span>
              <span>{fmtCurrency(freight)}</span>
            </div>
            <div className="flex justify-between items-center text-sap-textLight gap-2">
              <span className="flex items-center gap-1">
                <input type="checkbox" checked={form.rounding} onChange={e => setField('rounding', e.target.checked)} className={chk} />
                Rounding
                {form.rounding && (
                  <input type="number" value={form.rounding_amount} onChange={e => setField('rounding_amount', e.target.value)}
                    className="w-16 border border-sap-border rounded px-1 py-0.5 text-xs text-right focus:outline-none focus:border-sap-blue" />
                )}
              </span>
              <span>{fmtCurrency(roundingVal)}</span>
            </div>
            <div className="flex justify-between items-center text-sap-textLight">
              <span className="flex items-center gap-1">Tax
                <input type="number" value={form.tax_pct} onChange={e => setField('tax_pct', e.target.value)}
                  className="w-14 border border-sap-border rounded px-1 py-0.5 text-xs text-right focus:outline-none focus:border-sap-blue" />
                %
              </span>
              <span>{fmtCurrency(taxAmt)}</span>
            </div>
            <div className="flex justify-between font-bold text-sap-text border-t border-sap-border/60 pt-1 text-sm">
              <span>Total ({form.currency})</span>
              <span>{fmtCurrency(grandTotal)}</span>
            </div>
          </div>
        </div>
      </div>

      {refPicker && (
        <SapReferencePickerDialog
          type={refPicker.type}
          current={lines[refPicker.idx]?.[refPicker.field] || ''}
          onSelect={(row) => {
            const code = refPicker.type === 'tax' ? row.tax_code : row.rule_code;
            updateLine(refPicker.idx, refPicker.field, code || '');
            setRefPicker(null);
          }}
          onClose={() => setRefPicker(null)}
        />
      )}
    </div>
  );
}

function F({ label, children, className = '' }) {
  return (
    <div className={className}>
      <label className={lbl}>{label}</label>
      {children}
    </div>
  );
}

function BusinessPartnerProjectField({ value, onChange }) {
  const [open, setOpen] = React.useState(false);

  return (
    <>
      <div className="relative">
        <input value={value} onChange={e => onChange(e.target.value)} className={`${inp} pr-7`} />
        <ThreeDotsPicker title="Choose Project" onClick={() => setOpen(true)} />
      </div>
      {open && (
        <ProjectPickerDialog
          current={value}
          onSelect={(p) => { onChange(p.project_code || ''); setOpen(false); }}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
